import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesAPI } from '../utils/api';
import '../css/ClientBySalesExecutiveList.css';

function ClientBySalesExecutiveList() {
    const { officerName } = useParams();
    const navigate = useNavigate();

    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);

    useEffect(() => {
        fetchClientsByOfficer(officerName, startDate, endDate);
    }, [officerName]);

    const fetchClientsByOfficer = async (name, start, end) => {
        try {
            setLoading(true);
            setError(null);

            const params = {};
            if (start) params.startDate = start;
            if (end) params.endDate = end;

            const response = await salesAPI.getClientsByOfficer(name, params);
            // console.log('API Response:', response);

            if (response.data && Array.isArray(response.data)) {
                // Ensure data has correct structure
                const formattedClients = response.data.map(client => ({
                    id: client.client_id || client.id,
                    client_name: client.client_name || client.name || `Customer ${client.id}`,
                    phone: client.phone || 'Not provided',
                    address: client.address || 'Address not available',
                    email: client.email || 'Email not provided',
                    contact_person: client.contact_person || 'Not specified',
                    current_balance: parseFloat(client.current_balance || 0),
                    period_balance: parseFloat(client.period_balance || 0)
                }));

                setClients(formattedClients);
                setFilteredClients(formattedClients);
            } else {
                throw new Error('Invalid response format');
            }

        } catch (err) {
            // console.error('Error fetching customers:', err);
            setError('Showing demo data. Please check backend connection.');

            // Demo data
            const demoData = [
                {
                    id: 1,
                    client_name: 'Acme Corporation',
                    phone: '+1 (555) 123-4567',
                    address: '123 Business St, New York, NY 10001',
                    email: 'contact@acmecorp.com',
                    contact_person: 'John Doe',
                    current_balance: 15000.50,
                    period_balance: 15000.50
                },
                {
                    id: 2,
                    client_name: 'Global Tech',
                    phone: '+1 (555) 987-6543',
                    address: '456 Tech Blvd, San Francisco, CA 94101',
                    email: 'info@globaltech.com',
                    contact_person: 'Sarah Johnson',
                    current_balance: 2400.00,
                    period_balance: 2400.00
                },
                {
                    id: 3,
                    client_name: 'Sunrise Enterprises',
                    phone: '+1 (555) 456-7890',
                    address: '789 Sunrise Ave, Miami, FL 33101',
                    email: 'contact@sunrise.com',
                    contact_person: 'Michael Chen',
                    current_balance: 0.00,
                    period_balance: 0.00
                }
            ];
            setClients(demoData);
            setFilteredClients(demoData);
        } finally {
            setLoading(false);
        }
    };

    // Filter logic
    useEffect(() => {
        const filtered = clients.filter(client =>
            client.client_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredClients(filtered);
        setCurrentPage(1); // Reset to first page on search
    }, [searchTerm, clients]);

    const handleViewCustomerDetails = (clientId) => {
        // Navigate to customer details page
        navigate(`/customer-sales/${clientId}`);
    };

    const handleBack = () => {
        navigate(-1);
    };

    // Pagination logic
    const indexOfLastClient = currentPage * itemsPerPage;
    const indexOfFirstClient = indexOfLastClient - itemsPerPage;
    const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleExportCSV = () => {
        const headers = ['Client Name', 'Salesman', 'Balance'];
        const csvRows = [headers.join(',')];

        filteredClients.forEach(client => {
            const row = [
                `"${client.client_name.replace(/"/g, '""')}"`,
                `"${officerName}"`,
                (client.current_balance || 0).toFixed(2)
            ];
            csvRows.push(row.join(','));
        });

        // Add Grand Total
        const grandTotal = filteredClients.reduce((acc, curr) => acc + (curr.current_balance || 0), 0);
        csvRows.push(['"Grand Total"', '""', grandTotal.toFixed(2)].join(','));

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Sales_Manwise_Report_${officerName}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleApplyFilter = () => {
        fetchClientsByOfficer(officerName, startDate, endDate);
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
        fetchClientsByOfficer(officerName, '', '');
    };

    return (
        <div className="client-by-officer-container">
            {/* Header */}
            <div className="page-header mb-4">
                <button
                    className="btn btn-outline-secondary mb-3"
                    onClick={handleBack}
                >
                    ← Back to Sales
                </button>
                <h1>Customers for Sales Executive: {officerName}</h1>
                <p className="text-muted">
                    Viewing all customers managed by {officerName}
                </p>
                <button
                    className="btn btn-info text-white"
                    onClick={() => navigate(`/sales-executive-summary/${officerName}`)}
                >
                    <i className="bi bi-graph-up me-2"></i>
                    View Performance Summary
                </button>
            </div>

            {/* Search Bar - Keeping this for the customer cards at the top */}
            {!loading && !error && (
                <div className="search-container mb-4">
                    <div className="input-group">
                        <span className="input-group-text bg-white">
                            <i className="bi bi-search text-primary"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search client by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading customers...</p>
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div className="alert alert-warning" role="alert">
                    {error}
                </div>
            )}

            {/* Customers Grid - 3 cards per row */}
            {!loading && !error && (
                <>
                    {filteredClients.length === 0 ? (
                        <div className="alert alert-info" role="alert">
                            {searchTerm ? `No customers found matching "${searchTerm}"` : `No customers found for ${officerName}`}
                        </div>
                    ) : (
                        <>
                            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                                {currentClients.map((client) => (
                                    <div key={client.id} className="col">
                                        <div className="card h-100 customer-card">
                                            <div className="card-header bg-primary text-white">
                                                <h5 className="card-title mb-0">{client.client_name}</h5>
                                            </div>
                                            <div className="card-body">
                                                {/* Contact Info */}
                                                <div className="customer-info">
                                                    <div className="info-item mb-3">
                                                        <h6 className="text-muted small mb-2">Contact Information</h6>
                                                        <div className="mb-2">
                                                            <i className="bi bi-telephone me-2"></i>
                                                            <span>{client.phone || 'Phone not provided'}</span>
                                                        </div>
                                                        <div className="mb-2">
                                                            <i className="bi bi-envelope me-2"></i>
                                                            <span>{client.email || 'Email not provided'}</span>
                                                        </div>
                                                        <div className="mb-2">
                                                            <i className="bi bi-person me-2"></i>
                                                            <span>Contact: {client.contact_person || 'Not specified'}</span>
                                                        </div>
                                                    </div>

                                                    {/* Address */}
                                                    <div className="info-item mb-3">
                                                        <h6 className="text-muted small mb-2">Address</h6>
                                                        <div>
                                                            <i className="bi bi-geo-alt me-2"></i>
                                                            <span className="small">
                                                                {client.address || 'Address not available'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Outstanding Balance */}
                                                    <div className="info-item">
                                                        <h6 className="text-muted small mb-2">Account Balance</h6>
                                                        <div className={`fw-bold ${parseFloat(client.current_balance || 0) > 0 ? 'text-danger' : 'text-success'}`}>
                                                            <i className="bi bi-currency-rupee me-1"></i>
                                                            {new Intl.NumberFormat('en-IN', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            }).format(parseFloat(client.current_balance || 0))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-footer">
                                                <button
                                                    className="btn btn-primary w-100"
                                                    onClick={() => handleViewCustomerDetails(client.id)}
                                                >
                                                    <i className="bi bi-eye me-2"></i>
                                                    View Customer Details
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination Controls */}
                            {filteredClients.length > itemsPerPage && (
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div className="text-muted small">
                                        Showing {indexOfFirstClient + 1} to {Math.min(indexOfLastClient, filteredClients.length)} of {filteredClients.length} customers
                                    </div>
                                    <nav aria-label="Page navigation">
                                        <ul className="pagination mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => paginate(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    Previous
                                                </button>
                                            </li>
                                            {(() => {
                                                const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
                                                const maxVisible = 5;
                                                let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                                                let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                                                if (endPage - startPage + 1 < maxVisible) {
                                                    startPage = Math.max(1, endPage - maxVisible + 1);
                                                }

                                                return [...Array(endPage - startPage + 1)].map((_, index) => {
                                                    const pageNumber = startPage + index;
                                                    return (
                                                        <li
                                                            key={pageNumber}
                                                            className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                                                        >
                                                            <button
                                                                className="page-link"
                                                                onClick={() => paginate(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        </li>
                                                    );
                                                });
                                            })()}
                                            <li className={`page-item ${currentPage === Math.ceil(filteredClients.length / itemsPerPage) ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => paginate(currentPage + 1)}
                                                    disabled={currentPage === Math.ceil(filteredClients.length / itemsPerPage)}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </>
                    )}

                    {/* Net Balances Report Table */}
                    <div className="mt-5 mb-4 report-table-container shadow-lg border-0 rounded-4 overflow-hidden">
                        <div className="report-header p-4 text-center text-white" style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}>
                            <h4 className="fw-bold text-uppercase mb-3" style={{ letterSpacing: '2px' }}>Sales Manwise Report</h4>

                            {/* Integrated Filter Bar */}
                            <div className="report-filter-bar bg-white bg-opacity-10 p-3 rounded-3 mb-3 border border-white border-opacity-25">
                                <div className="row g-2 align-items-center">
                                    <div className="col-lg-3 col-md-6 mb-2">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white bg-opacity-25 border-0 text-white">From</span>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm bg-white bg-opacity-10 text-white border-0"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-6 mb-2">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white bg-opacity-25 border-0 text-white">To</span>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm bg-white bg-opacity-10 text-white border-0"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-8 mb-2">
                                        <div className="row g-2">
                                            <div className="col-6">
                                                <button className="btn btn-sm btn-light w-100" onClick={handleApplyFilter}>
                                                    <i className="bi bi-filter me-1"></i>
                                                    Filter
                                                </button>
                                            </div>
                                            <div className="col-6">
                                                <button className="btn btn-sm btn-outline-light w-100" onClick={handleClearDates}>
                                                    <i className="bi bi-x-circle me-1"></i>
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-2 col-md-4 mb-2 text-end">
                                        <button className="btn btn-sm btn-success w-100" onClick={handleExportCSV}>
                                            <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                                            Export CSV
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-center align-items-center gap-3">
                                <span className="badge bg-white bg-opacity-25 text-white border border-white border-opacity-25 px-3 py-2 rounded-pill">
                                    <i className="bi bi-person-fill me-2"></i>
                                    {officerName}
                                </span>
                                <span className="badge bg-white bg-opacity-25 text-white border border-white border-opacity-25 px-3 py-2 rounded-pill">
                                    <i className="bi bi-calendar3 me-2"></i>
                                    {startDate && endDate ? `${startDate} to ${endDate}` : new Date().toLocaleDateString('en-GB')}
                                </span>
                            </div>
                        </div>

                        <div className="table-responsive bg-white p-3">
                            <table className="table table-hover align-middle custom-report-table">
                                <thead>
                                    <tr>
                                        <th className="ps-4 py-3">Client Name</th>
                                        <th className="py-3">Executive</th>
                                        <th className="text-end pe-4 py-3">Current Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentClients.map(client => (
                                        <tr key={client.id}>
                                            <td className="ps-4 fw-medium text-dark">{client.client_name}</td>
                                            <td>
                                                <span className="text-muted small">
                                                    <i className="bi bi-briefcase me-1"></i>
                                                    {officerName}
                                                </span>
                                            </td>
                                            <td className="text-end pe-4 fw-bold">
                                                <div className={`price-tag ${client.current_balance > 0 ? 'debit' : 'credit'}`}>
                                                    {new Intl.NumberFormat('en-IN', {
                                                        style: 'currency',
                                                        currency: 'INR'
                                                    }).format(client.current_balance || 0)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light border-top">
                                    <tr className="fw-bold">
                                        <td colSpan="2" className="ps-4 py-3 fs-5">Combined Grand Total (All Results)</td>
                                        <td className="text-end pe-4 py-3 fs-5 text-primary">
                                            {new Intl.NumberFormat('en-IN', {
                                                style: 'currency',
                                                currency: 'INR'
                                            }).format(filteredClients.reduce((acc, curr) => acc + (curr.current_balance || 0), 0))}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        
                        {/* Table Pagination Info */}
                        {filteredClients.length > itemsPerPage && (
                            <div className="p-3 border-top bg-light d-flex justify-content-between align-items-center">
                                <div className="text-muted small">
                                    Showing {indexOfFirstClient + 1} to {Math.min(indexOfLastClient, filteredClients.length)} of {filteredClients.length} clients
                                </div>
                                <nav aria-label="Table Page navigation">
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
                                        </li>
                                        {[...Array(Math.ceil(filteredClients.length / itemsPerPage))].map((_, i) => (
                                            <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => paginate(i + 1)}>{i + 1}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === Math.ceil(filteredClients.length / itemsPerPage) ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage + 1)} disabled={currentPage === Math.ceil(filteredClients.length / itemsPerPage)}>Next</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>

                    {/* Summary */}
                    <div className="mt-4 p-3 bg-light rounded">
                        <div className="row">
                            <div className="col-md-6">
                                <h5>Summary</h5>
                                <p className="mb-1">
                                    <strong>Sales Executive:</strong> {officerName}
                                </p>
                                <p className="mb-0">
                                    <strong>Total Customers:</strong> {filteredClients.length} {searchTerm && `(filtered from ${clients.length})`}
                                </p>
                            </div>
                            <div className="col-md-6 text-md-end">
                                <button
                                    className="btn btn-outline-secondary mt-2"
                                    onClick={() => window.print()}
                                >
                                    <i className="bi bi-printer me-2"></i>
                                    Print List
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default ClientBySalesExecutiveList;